import { useCallback } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '@/contexts/ThemeContext';
import Pressable from '@/components/base/Pressable';
import { useAuth } from '@/contexts/AuthContext';

interface DemoGateProps {
  feature: string;
  open: boolean;
  onClose: () => void;
}

const featureMessages: Record<string, { title: string; subtitle: string; icon: string; benefit: string }> = {
  scan: {
    title: 'Create your free account to continue learning',
    subtitle: 'Save your progress and unlock more features',
    icon: 'ri-scan-line',
    benefit: 'Unlimited homework scans',
  },
  ai_chat: {
    title: 'Create your free account to continue learning',
    subtitle: 'Your AI tutor is waiting to help you more',
    icon: 'ri-bard-line',
    benefit: 'Unlimited AI tutoring',
  },
  video_preview: {
    title: 'Create your free account to continue learning',
    subtitle: 'Continue your study journey with video lessons',
    icon: 'ri-video-line',
    benefit: 'Unlimited study videos',
  },
  default: {
    title: 'Create your free account to continue learning',
    subtitle: 'Save your progress and unlock more features',
    icon: 'ri-sparkling-line',
    benefit: 'Full learning experience',
  },
};

export default function DemoGate({ feature, open, onClose }: DemoGateProps) {
  const navigate = useNavigate();
  const { resolvedTheme } = useTheme();
  const { isAuthenticated } = useAuth();
  const isDark = resolvedTheme === 'dark';

  const msg = featureMessages[feature] || featureMessages.default;

  const handleCreateAccount = useCallback(() => {
    navigate('/auth/signup');
    onClose();
  }, [navigate, onClose]);

  const handleLogIn = useCallback(() => {
    navigate('/auth/login');
    onClose();
  }, [navigate, onClose]);

  // Belt-and-suspenders: never show for authenticated users
  if (isAuthenticated) return null;

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.25 }}
          className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center"
          onClick={onClose}
        >
          {/* Backdrop */}
          <div className={`absolute inset-0 ${isDark ? 'bg-black/70' : 'bg-black/40'} backdrop-blur-sm`} />

          {/* Modal */}
          <motion.div
            initial={{ y: 100, opacity: 0, scale: 0.95 }}
            animate={{ y: 0, opacity: 1, scale: 1 }}
            exit={{ y: 60, opacity: 0, scale: 0.95 }}
            transition={{ type: 'spring', stiffness: 400, damping: 30 }}
            className={`relative w-full sm:w-[420px] sm:rounded-3xl rounded-t-3xl ${isDark ? 'bg-[#1C1D21] border-t border-white/[0.06]' : 'bg-white border-t border-neutral-100'} px-6 pt-8 pb-8 sm:px-8 sm:pt-10 sm:pb-10`}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Decorative glow */}
            <div className={`absolute top-0 left-1/2 -translate-x-1/2 w-32 h-32 rounded-full blur-3xl opacity-20 pointer-events-none ${isDark ? 'bg-white' : 'bg-neutral-900'}`} />

            {/* Icon */}
            <div className="relative flex justify-center mb-5">
              <div className={`w-16 h-16 rounded-2xl flex items-center justify-center ${isDark ? 'bg-white/[0.06] border border-white/[0.08]' : 'bg-neutral-50 border border-neutral-100'}`}>
                <div className="w-8 h-8 flex items-center justify-center">
                  <i className={`${msg.icon} ${isDark ? 'text-white' : 'text-neutral-900'} text-2xl`} />
                </div>
              </div>
            </div>

            {/* Text */}
            <div className="relative text-center mb-6">
              <h2 className={`text-lg font-bold ${isDark ? 'text-white' : 'text-neutral-900'} mb-2 leading-snug`}>
                {msg.title}
              </h2>
              <p className={`text-sm ${isDark ? 'text-neutral-400' : 'text-neutral-500'} leading-relaxed`}>
                {msg.subtitle}
              </p>
            </div>

            {/* Benefits */}
            <div className="relative space-y-2.5 mb-7">
              {[
                { icon: 'ri-scan-line', text: 'Unlimited homework scans' },
                { icon: 'ri-bard-line', text: 'Unlimited AI tutoring' },
                { icon: 'ri-video-line', text: 'Study videos & explanations' },
                { icon: 'ri-trophy-line', text: 'Track progress & earn XP' },
                { icon: 'ri-flashlight-line', text: 'Priority AI processing' },
              ].map((item) => (
                <div key={item.text} className={`flex items-center gap-3 px-4 py-2.5 rounded-xl ${isDark ? 'bg-white/[0.03]' : 'bg-neutral-50'}`}>
                  <div className="w-5 h-5 flex items-center justify-center">
                    <i className={`${item.icon} text-emerald-500 text-sm`} />
                  </div>
                  <span className={`text-sm font-medium ${isDark ? 'text-neutral-300' : 'text-neutral-700'}`}>{item.text}</span>
                </div>
              ))}
            </div>

            {/* CTAs */}
            <div className="relative space-y-3">
              <Pressable
                onClick={handleCreateAccount}
                className="w-full bg-neutral-900 text-white py-3.5 rounded-2xl text-sm font-semibold flex items-center justify-center gap-2"
                scale={0.97}
              >
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-user-add-line text-sm" />
                </div>
                <span className="whitespace-nowrap">Sign Up Free</span>
              </Pressable>

              <Pressable
                onClick={handleLogIn}
                className={`w-full ${isDark ? 'bg-white/[0.04] text-white border border-white/[0.06]' : 'bg-white text-neutral-900 border border-neutral-200'} py-3.5 rounded-2xl text-sm font-semibold flex items-center justify-center gap-2`}
                scale={0.97}
              >
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-login-box-line text-sm" />
                </div>
                <span className="whitespace-nowrap">Log In</span>
              </Pressable>

              <Pressable
                onClick={onClose}
                className={`w-full ${isDark ? 'bg-white/[0.02] text-neutral-500 border border-white/[0.04]' : 'bg-neutral-50 text-neutral-400 border border-neutral-100'} py-3 rounded-2xl text-xs font-medium flex items-center justify-center`}
                scale={0.97}
              >
                <span className="whitespace-nowrap">Maybe Later</span>
              </Pressable>
            </div>

            {/* Trust line */}
            <p className={`text-center text-[11px] ${isDark ? 'text-neutral-600' : 'text-neutral-400'} mt-4`}>
              No credit card required. Free forever for students.
            </p>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}